<template>
  <pt-sheet title="Marketplace Profile">
    <div v-if="loaded && !profile">
      <v-divider class="mb-2" />
      <p class="subtitle-2 mb-4 colorNeutralsNavy400--text">
        The VendorProof Marketplace is an online directory of preferred vendors
        whom healthcare providers can rely on as trusted business partners.
        Complete the profile below to be promoted to some of the top companies
        in the healthcare sector.
      </p>
      <p
        v-if="showEditButton"
        class="subtitle-2 mb-4 colorNeutralsNavy400--text"
      >
        Not ready to join today? No problem - you can log in to VendorProof at
        any time to complete your Marketplace profile!
      </p>
      <p v-else class="subtitle-2 mb-4 colorNeutralsNavy400--text">
        <!-- If we're not showing the edit buttons, we must be in signup, so it's safe to reference the "Next" button. -->
        Not ready to join today? No problem! Just click Next and you can
        complete it later.
      </p>
    </div>

    <div class="text-right" v-if="loaded && !profile">
      <yellow-button
        @click="
          logEvent('Clicked button to create new profile');
          profile = {};
          controls.editMode = true;
        "
        >Create Profile
      </yellow-button>
    </div>
    <template v-else-if="loaded && profile">
      <v-container class="text-right pa-sm-0 mr-sm-4" v-if="showEditButton">
        <v-btn
          v-if="!controls.editMode"
          text
          @click="
            logEvent('Clicked button to edit profile');
            controls.editMode = !controls.editMode;
          "
          class="edit-button"
          :block="$vuetify.breakpoint.xsOnly"
        >
          <v-icon center small class="edit-button-icon">mdi-pencil</v-icon>
          <span>Edit</span>
        </v-btn>
        <v-btn
          v-if="controls.editMode"
          text
          @click="cancel()"
          class="cancel-button"
          :block="$vuetify.breakpoint.xsOnly"
        >
          <span>Cancel</span>
        </v-btn>
        <v-btn
          v-if="controls.editMode"
          color="primary"
          @click="
            logEvent('Clicked button to save changes on profile');
            save();
          "
          class="save-changes-button"
          :block="$vuetify.breakpoint.xsOnly"
        >
          Save Changes
        </v-btn>
      </v-container>
      <v-form>
        <v-container pa-0>
          <v-row no-gutters>
            <v-col cols="12" lg="2">
              <div class="information-container-title">
                Display Name
              </div>
            </v-col>
            <v-col cols="12" lg="10">
              <v-row no-gutters>
                <v-col cols="12" md="4">
                  <v-text-field
                    filled
                    dense
                    v-model="profile.name"
                    label="Display Name"
                    :disabled="!controls.editMode"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-divider class="my-8"></v-divider>
          <v-row no-gutters>
            <v-col cols="12" lg="2">
              <div class="information-container-title">
                Logo
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <file-upload-classic
                :accepted-files="{
                  PNG: 'image/png',
                  JPG: 'image/jpeg',
                  GIF: 'image/gif'
                }"
                :dropzone-options="dropzoneOptions"
                :existing-file="
                  profile.logoFileId
                    ? {
                        id: profile.logoFileId,
                        previewUrl: profile.logoUrl,
                        fileName: profile.logoName
                      }
                    : null
                "
                @input="profile.logoFileId = $event"
                :disabled="!controls.editMode"
              >
              </file-upload-classic>
            </v-col>
          </v-row>
          <v-divider class="my-8"></v-divider>
          <v-row no-gutters>
            <v-col cols="12" lg="2">
              <div class="information-container-title">
                Business Description
              </div>
            </v-col>
            <v-col cols="12" lg="10">
              <v-row no-gutters>
                <v-col cols="12" md="7">
                  <v-textarea
                    filled
                    dense
                    rows="2"
                    v-model="profile.summary"
                    label="Summary"
                    hint="Give a brief summary of what service(s) your business provides"
                    persistent-hint
                    :disabled="!controls.editMode"
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-row no-gutters class="mt-4">
                <v-col cols="12">
                  <v-textarea
                    filled
                    dense
                    auto-grow
                    v-model="profile.description"
                    label="Description"
                    :disabled="!controls.editMode"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-divider class="my-8"></v-divider>
          <v-row no-gutters>
            <v-col cols="12" lg="2">
              <div class="information-container-title">
                States Serviced
              </div>
            </v-col>
            <v-col cols="12" lg="10">
              <v-row no-gutters>
                <v-col cols="12" md="2">
                  <v-checkbox
                    dense
                    label="All"
                    class="mt-0"
                    v-model="controls.allStatesSelected"
                    @change="checkboxUpdated"
                    :disabled="!controls.editMode"
                  >
                  </v-checkbox>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="!controls.allStatesSelected">
                <v-col cols="12" md="6">
                  <v-select
                    filled
                    dense
                    clearable
                    multiple
                    chips
                    v-model="profile.states"
                    :items="states"
                    item-value="state"
                    item-text="code"
                    label="Select states"
                    hint="Select the states your services are made available"
                    persistent-hint
                    :disabled="!controls.editMode"
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-divider class="my-8"></v-divider>
          <v-row no-gutters>
            <v-col cols="12" lg="2">
              <div class="information-container-title">
                Categories
              </div>
            </v-col>
            <v-col cols="12" lg="10">
              <v-row no-gutters>
                <v-col cols="12" md="6">
                  <v-select
                    filled
                    dense
                    clearable
                    multiple
                    chips
                    v-model="profile.categories"
                    :items="categories"
                    item-value="id"
                    item-text="name"
                    return-object
                    label="Select categories"
                    hint="Choose one or more categories your business fits in"
                    persistent-hint
                    :disabled="!controls.editMode"
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-divider class="my-8"></v-divider>
          <v-row no-gutters>
            <v-col cols="12" lg="2">
              <div class="information-container-title">
                Contact Information
              </div>
            </v-col>
            <v-col cols="12" lg="10">
              <v-row no-gutters>
                <v-col cols="12" md="3" lg="2">
                  <v-text-field
                    filled
                    dense
                    class="mr-md-2"
                    v-model="profile.contactFirstName"
                    label="First Name"
                    :disabled="!controls.editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3" lg="2">
                  <v-text-field
                    filled
                    dense
                    class="mr-md-2"
                    v-model="profile.contactMiddleName"
                    label="Middle Name"
                    :disabled="!controls.editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3" lg="2">
                  <v-text-field
                    filled
                    dense
                    class="mr-md-2"
                    v-model="profile.contactLastName"
                    label="Last Name"
                    :disabled="!controls.editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2" lg="1">
                  <v-text-field
                    filled
                    dense
                    v-model="profile.contactSuffix"
                    label="Suffix"
                    :disabled="!controls.editMode"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12" md="5">
                  <v-text-field
                    filled
                    dense
                    v-model="profile.contactEmail"
                    label="Email"
                    :disabled="!controls.editMode"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12" md="4">
                  <v-radio-group
                    dense
                    row
                    v-model="profile.contactPhoneUS"
                    :disabled="!controls.editMode"
                  >
                    <v-radio label="US/Canada" :value="true" />
                    <v-radio label="Outside US" :value="false" />
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12" md="3">
                  <v-text-field
                    filled
                    dense
                    class="mr-2"
                    v-model="profile.contactPhone"
                    v-mask="
                      profile.contactPhoneUS
                        ? controls.phoneMaskUS
                        : controls.phoneMaskIntl
                    "
                    label="Phone Number"
                    :disabled="!controls.editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    filled
                    dense
                    v-model="profile.contactPhoneExt"
                    label="Extension"
                    :disabled="!controls.editMode"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-divider class="my-8"></v-divider>
          <v-row no-gutters>
            <v-col cols="12" lg="2">
              <div class="information-container-title">
                Website
              </div>
            </v-col>
            <v-col cols="12" lg="10">
              <v-row no-gutters>
                <v-col cols="12" md="5">
                  <v-text-field
                    filled
                    dense
                    v-model="profile.website"
                    label="Website"
                    :disabled="!controls.editMode"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-divider class="my-8"></v-divider>
          <v-row no-gutters>
            <v-col cols="12" lg="2">
              <div class="information-container-title">
                Display profile on Marketplace
              </div>
            </v-col>
            <v-col cols="12" lg="10">
              <v-row no-gutters>
                <v-col cols="12" md="9">
                  <v-switch
                    inset
                    class="mt-0"
                    v-model="profile.visible"
                    :disabled="!controls.editMode"
                    hint="If left disabled, your profile will be hidden from the Marketplace"
                    persistent-hint
                  ></v-switch>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </template>
  </pt-sheet>
</template>

<script>
import { mask } from "vue-the-mask";
import {
  marketplaceCategoriesAPI,
  marketplaceProfileAPI
} from "@/services/api";
import { get, put } from "@/services/request";
import cloneDeep from "lodash/cloneDeep";
import * as rules from "@/services/rules";
import hasConstant from "@/mixins/has-constant";
import YellowButton from "@/components/providertrust/buttons/SmallYellowButton";
import FileUploadClassic from "@/components/FileUploadClassic";
import hasNotifications from "@/mixins/has-notifications";
import PtSheet from "@/components/PtSheet.vue";

const dropzoneOptions = {
  url: "/services/files",
  headers: {
    "user-type": "vendor",
    "directory-name": "marketplace-logo"
  },
  maxFilesize: 0.8
};

export default {
  name: "MarketplaceProfile",
  created() {
    get(marketplaceProfileAPI)
      .then(response => this.handleResponse(response))
      .finally(() => (this.loaded = true));
    this.loadCategories();
  },
  props: {
    showEditButton: {
      type: Boolean,
      default: true
    },
    editable: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      profile: null,
      existingProfile: null,
      categories: [],
      rules,
      loaded: false,
      controls: {
        editMode: this.editable,
        phoneMaskUS: "###-###-####",
        phoneMaskIntl: "+###############",
        allStatesSelected: false
      },
      dropzoneOptions
    };
  },
  directives: {
    mask
  },
  methods: {
    reset() {
      this.profile = cloneDeep(this.existingProfile);
    },
    cancel() {
      this.controls.editMode = false;
      this.reset();
    },
    save() {
      this.controls.editMode = false;
      if (this.profile) {
        return put(marketplaceProfileAPI, this.profile)
          .then(response => {
            this.handleResponse(response);
            if (this.editable) {
              //Leave component in editMode
              this.controls.editMode = true;
            }
          })
          .catch(() => {
            this.showMessage({
              text: "Could not save marketplace profile",
              mode: "error"
            });
            this.reset();
            return Promise.reject();
          });
      }
      return Promise.resolve();
    },
    checkboxUpdated(newValue) {
      if (newValue) this.profile.states.length = 0;
    },
    handleResponse(response) {
      if (response && response.data) {
        this.profile = response.data;
        this.existingProfile = cloneDeep(response.data);
      }
    },
    loadCategories() {
      get(marketplaceCategoriesAPI).then(response => {
        if (response && response.data) {
          this.categories = response.data;
        }
      });
    }
  },
  mixins: [hasConstant, hasNotifications],
  components: {
    PtSheet,
    YellowButton,
    FileUploadClassic
  }
};
</script>

<style lang="sass" scoped>
.cancel-btn
  color: $lightish-grey

.information-container-title
  margin-bottom: 16px
</style>
